/** Form fields that may be filled from PhilHealth registration autofill. */
export const PHILHEALTH_AUTOFILL_FIELD_NAMES = new Set([
  "last_name",
  "first_name",
  "middle_name",
  "suffix",
  "birthdate",
  "contact_no",
  "email",
  "sex",
  "country",
  "province",
  "city",
  "barangay",
  "street",
  "zip_code",
  "civil_status",
  "occupation",
  "nationality",
  "religion",
  "emergency_contact",
  "emergency_contact_no",
  "relation_w_contact",
  "senior_id",
  "pwd_id",
  "is_pwd",
  "ph_check",
  "phic_membership_type",
  "phic_pin",
  "phic_dependent_pin",
  "phic_last_name",
  "phic_first_name",
  "phic_middle_name",
  "phic_extension_name",
  "phic_birthdate",
  "phic_contact_no",
  "phic_enlistment_date",
  "phic_enlistment_status",
  "phic_package_type",
  "phic_atc",
]);

export const PHIC_MEMBER_SYNC_FROM_PATIENT = {
  phic_last_name: "last_name",
  phic_first_name: "first_name",
  phic_middle_name: "middle_name",
  phic_extension_name: "suffix",
  phic_birthdate: "birthdate",
  phic_contact_no: "contact_no",
};

export const hasAutofillValue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "number") return !Number.isNaN(value);
  if (typeof value === "boolean") return value;
  return false;
};

const CIVIL_STATUS_VALUES = new Set([
  "child",
  "single",
  "married",
  "divorced",
  "separated",
  "widowed",
]);

/** Align autofill keys with what Add Patient applies to the form (incl. phic_* aliases). */
export const normalizePhilhealthAutofillForLocks = (autofillData = {}) => {
  const autofill =
    autofillData && typeof autofillData === "object" ? { ...autofillData } : {};

  autofill.last_name =
    autofill.last_name || autofill.phic_last_name || "";
  autofill.first_name =
    autofill.first_name || autofill.phic_first_name || "";
  autofill.middle_name =
    autofill.middle_name || autofill.phic_middle_name || "";
  autofill.suffix =
    autofill.suffix || autofill.phic_extension_name || "";
  autofill.birthdate =
    autofill.birthdate || autofill.phic_birthdate || "";
  autofill.contact_no =
    autofill.contact_no ||
    autofill.mobile_number ||
    autofill.phic_contact_no ||
    "";

  return autofill;
};

const getAutofillValueForField = (field, autofill, navState = {}) => {
  switch (field) {
    case "last_name":
      return autofill.last_name || autofill.phic_last_name;
    case "first_name":
      return autofill.first_name || autofill.phic_first_name;
    case "middle_name":
      return autofill.middle_name || autofill.phic_middle_name;
    case "suffix":
      return autofill.suffix || autofill.phic_extension_name;
    case "birthdate":
      return autofill.birthdate || autofill.phic_birthdate;
    case "contact_no":
      return autofill.contact_no || autofill.mobile_number || autofill.phic_contact_no;
    case "phic_pin":
      return autofill.phic_pin || navState.phic_pin;
    case "phic_dependent_pin":
      return autofill.phic_dependent_pin || navState.phic_dependent_pin;
    case "phic_membership_type":
      return autofill.phic_membership_type || navState.phic_membership_type;
    case "phic_atc":
      return autofill.phic_atc || navState.atc;
    default:
      return autofill[field];
  }
};

/** Avoid locking fields that only received app/form defaults, not real PhilHealth data. */
const shouldLockPhilhealthField = (field, value, autofill) => {
  if (!hasAutofillValue(value)) return false;

  if (field === "is_pwd") {
    return value === "1" || value === 1 || hasAutofillValue(autofill.pwd_id);
  }

  if (field === "pwd_id" || field === "senior_id") {
    return hasAutofillValue(value);
  }

  if (field === "country" || field === "nationality") {
    const hasAddressFromPhic =
      hasAutofillValue(autofill.province) ||
      hasAutofillValue(autofill.city) ||
      hasAutofillValue(autofill.barangay) ||
      hasAutofillValue(autofill.street) ||
      hasAutofillValue(autofill.zip_code);
    if (!hasAddressFromPhic) return false;
  }

  if (field === "civil_status") {
    return CIVIL_STATUS_VALUES.has(String(value).trim().toLowerCase());
  }

  return true;
};

/** Fields with PhilHealth-provided values (read-only); empty fields stay editable. */
export const buildPhilhealthAutofillLocks = (autofillData, navState = {}) => {
  const locks = new Set();
  if (!navState?.fromPhilhealthRegistration) return locks;

  const autofill = normalizePhilhealthAutofillForLocks(autofillData);

  PHILHEALTH_AUTOFILL_FIELD_NAMES.forEach((field) => {
    const value = getAutofillValueForField(field, autofill, navState);
    if (shouldLockPhilhealthField(field, value, autofill)) {
      locks.add(field);
    }
  });

  return locks;
};

export const isPhilhealthFieldLocked = (fieldName, locks, membershipType) => {
  if (!fieldName || !(locks instanceof Set) || locks.size === 0) return false;
  if (locks.has(fieldName)) return true;
  if (membershipType === "member") {
    const patientField = PHIC_MEMBER_SYNC_FROM_PATIENT[fieldName];
    if (patientField && locks.has(patientField)) return true;
  }
  return false;
};
